import React, { useState } from 'react';
import { BILLING_DECIMAL_PLACES } from '../../../constants/billingConstants';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Divider,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import HistoryIcon from '@material-ui/icons/History';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import InfoIcon from '@material-ui/icons/Info';

/**
 * Master Sheet Tab Component
 * Displays the master billing items table
 */
const MasterSheetTab = ({
  classes,
  search,
  onSearchChange,
  filteredMaster,
  allFlatOperatorItems,
  onOpenDetail,
  onOpenHistory,
  onExport,
  onCsvUpload,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  return (
    <>
      {/* Help Dialog */}
      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            Master Sheet CSV Export/Import Guide
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>
            📋 Overview
          </Typography>
          <Typography paragraph>
            The Master Sheet contains the default billing items and pricing that apply to all operators.
            You can export this data to CSV, make bulk changes, and import it back.
          </Typography>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            📊 CSV Columns Explained
          </Typography>

          <Box ml={2}>
            <Typography variant="body2" paragraph>
              • <strong>Billing Item Name:</strong> Internal unique identifier for this billing item (e.g., "basicCharge-plan01s-active")
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Display Name:</strong> Customer-facing name shown on invoices. Multiple items can share the same display name
              to be grouped on invoices (e.g., "SIM Fee", "Data Usage", "Monthly Charge")
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Charge Code:</strong> Internal accounting code for your finance system (e.g., CHG001, CHG002)
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Unit:</strong> Unit of measurement for this charge (e.g., DAYS, per_month, per_MB, per_device)
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Soracom Invoice Item Name:</strong> Exact name as it appears on Soracom's invoice to Thinxtra
              (used for reconciliation and matching)
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Master Purchase Price (USD):</strong> Default cost price in USD - what Thinxtra pays (numeric only, e.g., 1.05)
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Master Sale Price (USD):</strong> Default selling price in USD - what operators are charged (numeric only, e.g., 1.25)
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>GST Applicable:</strong> Whether GST tax applies to this item. Must be exactly "TRUE" or "FALSE"
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>GST Percentage:</strong> Tax rate as a number (e.g., 10 for 10% GST in Australia)
            </Typography>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            📝 Example CSV Row
          </Typography>
          <Paper style={{ padding: 12, backgroundColor: '#f5f5f5', overflowX: 'auto' }}>
            <Typography variant="caption" component="div" style={{ fontFamily: 'monospace', fontSize: 10 }}>
              Billing Item Name: basicCharge-plan01s-active<br />
              Display Name: Basic Plan Charge<br />
              Charge Code: CHG001<br />
              Unit: DAYS<br />
              Soracom Invoice Item Name: Basic Charge - Plan 01s Active<br />
              Master Purchase Price (USD): 1.05<br />
              Master Sale Price (USD): 1.25<br />
              GST Applicable: TRUE<br />
              GST Percentage: 10
            </Typography>
          </Paper>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom>
            💡 Tips
          </Typography>
          <Box ml={2}>
            <Typography variant="body2">• Multiple items can share the same "Display Name" - they'll be grouped and summed on customer invoices</Typography>
            <Typography variant="body2">• Master prices apply to all operators by default unless they have custom operator overrides</Typography>
            <Typography variant="body2">• When you update master prices, operators using "Use Master Pricing = TRUE" will automatically get the new prices</Typography>
            <Typography variant="body2">• "Billing Item Name" must match exactly with what comes from Soracom's API</Typography>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          <Typography variant="h6" gutterBottom color="error">
            ⚠️ Important Notes
          </Typography>
          <Box ml={2}>
            <Typography variant="body2">• <strong>Do NOT modify:</strong> "Billing Item Name" once created (it's the unique identifier)</Typography>
            <Typography variant="body2">• <strong>Price format:</strong> Must be numeric only (e.g., 1.5 not $1.50 or 1.5 USD)</Typography>
            <Typography variant="body2">• <strong>Boolean format:</strong> Must be exactly "TRUE" or "FALSE" in all caps</Typography>
            <Typography variant="body2">• <strong>All fields required:</strong> Every column must have a value</Typography>
            <Typography variant="body2">• <strong>Changes apply globally:</strong> Master sheet changes affect all operators (unless they have custom overrides)</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} color="primary">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          placeholder="Search billing item or display name…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Download master sheet as CSV">
          <Button
            size="small"
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={() => onExport('master')}
          >
            Export CSV
          </Button>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PublishIcon />}
          component="label"
        >
          Import CSV
          <input
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => onCsvUpload(e, 'master')}
          />
        </Button>
        <Tooltip title="Learn how CSV Export/Import works">
          <IconButton
            size="small"
            color="primary"
            onClick={() => setHelpDialogOpen(true)}
            style={{ marginLeft: 4 }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </div>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Billing Item Name</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Charge Code</TableCell>
              <TableCell>Soracom Invoice Item Name</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align="right">Purchase $</TableCell>
              <TableCell align="right">Sale $</TableCell>
              <TableCell>GST</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMaster.map((row) => {
              const excludedByCount = allFlatOperatorItems.filter(
                (o) =>
                  o.billingItemName === row.billingItemName &&
                  o.excludeFromBilling
              ).length;

              return (
                <TableRow key={row._id} hover>
                  <TableCell>{row.billingItemName}</TableCell>
                  <TableCell>{row.displayItemName}</TableCell>
                  <TableCell>
                    <code>{row.chargeCode}</code>
                  </TableCell>
                  <TableCell>{row.soracomInvoiceItemName}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell align="right">
                    ${row.purchasePrice.toFixed(BILLING_DECIMAL_PLACES)}
                  </TableCell>
                  <TableCell align="right">
                    ${row.salePrice.toFixed(BILLING_DECIMAL_PLACES)}
                  </TableCell>
                  <TableCell>
                    {row.GSTApplicable ? (
                      <Chip
                        size="small"
                        label={`${row.GSTPercentage}%`}
                        color="primary"
                      />
                    ) : (
                      <Chip size="small" label="No GST" />
                    )}
                    {excludedByCount > 0 && (
                      <Tooltip
                        title={`Excluded from billing for ${excludedByCount} operator${excludedByCount > 1 ? 's' : ''
                          }`}
                      >
                        <Chip
                          size="small"
                          label={`Excl. ${excludedByCount}`}
                          style={{
                            marginLeft: 4,
                            backgroundColor: '#fff3e0',
                            color: '#e65100',
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View / Edit">
                      <IconButton
                        size="small"
                        onClick={() => onOpenDetail(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View change history">
                      <IconButton
                        size="small"
                        onClick={() => onOpenHistory(row)}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[25, 50, 100, 250]}
      />
    </>
  );
};

export default MasterSheetTab;
